import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import moment from 'moment'
import { orderBy, indexOf } from 'lodash'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { SubscriptionTypeTypes, subscriptionTypes } from '@/modules/base/util/constants'
import { checkIsAdminRole } from '@/modules'
import { useUpgradeModal } from '@/hooks/useUpgradeModal'
import navigateToShop from '@/modules/moduleSubscription/components/_helpers/navigateToShop'
import { isMeinBuero } from '@/utils/build-env'
import { ButtonSquare } from '@/modules/base/components/buttons'
import { useAppSelector } from '@/redux/store'
import { ColorModes } from '@/modules/layout/constants'

const isMB = isMeinBuero()

const SUBSCRIPTIONS_ORDER = [SubscriptionTypeTypes.Staff, SubscriptionTypeTypes.Document, SubscriptionTypeTypes.Main]

interface SubscriptionData {
  type: string
  isTrial: boolean
  isExpired: boolean
  end: Date | string
  typeOfSubscription: string
  title?: string
}

interface AccountPopoverUpgradeBoxProps {
  onClose: () => void
}

const staffEmployeesMap: Record<string, number> = {
  [subscriptionTypes.STAFF_S]: 5,
  [subscriptionTypes.STAFF_M]: 10,
  [subscriptionTypes.STAFF_L]: 20,
  [subscriptionTypes.STAFF_XL]: 30,
}

const topPlansMap: Record<string, string[]> = {
  [SubscriptionTypeTypes.Main]: [subscriptionTypes.WARENWIRTSCHAFT_2026, subscriptionTypes.L_2025, subscriptionTypes.XL_2021, subscriptionTypes.L_2021_2],
  [SubscriptionTypeTypes.Document]: [subscriptionTypes.DOCUMENT_XL, subscriptionTypes.DOCUMENT_L_ERP, subscriptionTypes.DOCUMENT_L],
  [SubscriptionTypeTypes.Staff]: [subscriptionTypes.STAFF_XL],
}

const isTopPlan = (subscription: SubscriptionData): boolean => {
  const topPlans = topPlansMap[subscription.typeOfSubscription]
  if (!topPlans) return false
  return topPlans.includes(subscription.type)
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  upgradeBox: {
    padding: '20px 30px',
    backgroundColor: theme.mode.type === ColorModes.dark ? theme.mode.backgroundColor.medium : theme.backgroundColorPrimaryLight,
    borderRadius: 8,
    margin: '20px 30px 20px',
  },
  planTitle: {
    fontSize: theme.fontSizes.medium,
    fontWeight: theme.fontWeights.medium,
    color: theme.mode.color.default,
    marginBottom: 8,
  },
  planSubtitle: {
    fontSize: theme.fontSizes.small,
    color: theme.mode.color.medium,
    marginBottom: 16,
  },
  upgradeButton: {
    width: '100%',
  },
}), { name: 'AccountPopoverUpgradeBox' })

const getPlanTitle = (subscription: SubscriptionData) => {
  const { isTrial, title } = subscription

  if (isTrial) {
    return lang.myAccount.accountPopover.trialVersionTitle
  }

  return title || lang.myAccount.accountPopover.planTitle(subscription.type)
}

const getPlanSubtitle = (subscription: SubscriptionData, remainingDays: number) => {
  const { isTrial, type, typeOfSubscription } = subscription

  if (isTrial) {
    return lang.myAccount.accountPopover.planSubtitle(remainingDays)
  }
  if (typeOfSubscription === SubscriptionTypeTypes.Staff) {
    const employees = staffEmployeesMap[type]
    if (employees && typeof lang.upgradeModal.subtitles.staff === 'function') {
      return lang.upgradeModal.subtitles.staff(employees)
    }
  }

  const subtitle = lang.upgradeModal.subtitles[type]
  return subtitle || ''
}

const AccountPopoverUpgradeBox = ({ onClose }: AccountPopoverUpgradeBoxProps) => {
  const classes = useStyles()
  const openUpgradeModal = useUpgradeModal()
  const subscriptions = useAppSelector(state => state.subscription.details.subscriptions)
  const userInfo = useAppSelector(state => state.user.info)

  const canSeeUpgrade = userInfo.isOwner || userInfo.isAdminLogin || checkIsAdminRole(userInfo)

  const { topSubscription, shouldShowUpgrade, hasTopPlan, remainingDays } = useMemo(() => {
    if (!subscriptions?.length || !canSeeUpgrade) {
      return { topSubscription: null, shouldShowUpgrade: false, hasTopPlan: false, remainingDays: 0 }
    }

    // Get non-expired subscriptions only
    const activeSubscriptions = subscriptions.filter((sub: SubscriptionData) => !sub.isExpired)

    if (!activeSubscriptions.length) {
      return { topSubscription: null, shouldShowUpgrade: false, hasTopPlan: false, remainingDays: 0 }
    }

    const sortedByType = orderBy(
      activeSubscriptions,
      [obj => indexOf(SUBSCRIPTIONS_ORDER, obj.typeOfSubscription)],
      ['desc'],
    )

    const sortedSubscriptions = sortedByType.sort((a: SubscriptionData, b: SubscriptionData) => {
      if (a.typeOfSubscription === b.typeOfSubscription) {
        if (a.isTrial !== b.isTrial) {
          return a.isTrial ? 1 : -1
        }
        const aEnd = moment(a.end)
        const bEnd = moment(b.end)
        return bEnd.diff(aEnd)
      }
      return 0
    })

    const topSub = sortedSubscriptions[0]

    const userHasTopPlan = topSub && !topSub.isTrial && isTopPlan(topSub)
    const shouldShow = !!topSub

    const days = topSub.end
      ? Math.max(moment(topSub.end).diff(moment(), 'days'), 0)
      : 0

    return {
      topSubscription: topSub,
      shouldShowUpgrade: shouldShow,
      hasTopPlan: userHasTopPlan,
      remainingDays: days,
    }
  }, [subscriptions, canSeeUpgrade])

  const handleUpgradeClick = () => {
    if (!topSubscription) return

    onClose()

    if (isMB) {
      if (topSubscription.isTrial) {
        navigateToShop({ selectActiveTrialProduct: true, scrollIntoPlans: true })
      } else {
        navigateToShop({ product: topSubscription.typeOfSubscription })
      }
    } else {
      if (topSubscription.isTrial) {
        openUpgradeModal()
      } else {
        openUpgradeModal({
          activeSubscription: topSubscription,
          skipSubscriptionSelection: true,
          subscriptionType: topSubscription.typeOfSubscription,
          forceType: true,
        })
      }
    }
  }

  if (!shouldShowUpgrade || !topSubscription) {
    return null
  }

  const planTitle = getPlanTitle(topSubscription)
  const planSubtitle = getPlanSubtitle(topSubscription, remainingDays)
  const buttonLabel = topSubscription.isTrial ? lang.myAccount.accountPopover.startTrialButton : lang.myAccount.accountPopover.upgradeButton

  return (
    <div className={classes.upgradeBox}>
      <div className={classes.planTitle}>{planTitle}</div>
      <div className={classes.planSubtitle}>{planSubtitle}</div>
      {!hasTopPlan && (
        <ButtonSquare
          label={buttonLabel}
          onClick={handleUpgradeClick}
          className={classes.upgradeButton}
        />
      )}
    </div>
  )
}

export default AccountPopoverUpgradeBox
